import { playerEventTypes } from "../constants"

/*
  ViewMedica pushes two independent channels for a video (the vendor library
  is public/vm.js):

  - "analytics" postMessage events, fired automatically by the player:
    Brochure Printed / Video Loaded / Video Started / Percent Reached /
    Step Reached / Video End / Playlist events.
  - "state" — NOT pushed. The player exposes getPlayerState()/getCurrentTime()/
    getDuration() on window.OndemandPlayerApi, which must be polled to detect
    waiting/ready/played/paused transitions and accumulate watched time.
*/

/*
  Served from our own origin rather than the vendor's CDN: the app's CSP sets
  script-src 'self' (see the meta tag in public/index.html), which blocks
  https://ondemand.viewmedica.com/lib/vm.js outright. Without it there is no
  window.OndemandPlayerApi, so the state channel below never runs.

  public/vm.js is a byte-identical copy of that URL so it can be diffed against
  upstream — to update, re-download it there and leave it unmodified.
*/
const VIEWMEDICA_SCRIPT_SRC = "/vm.js"

// Every analyticType the player posts, named as vm.js names them. 6-8 are
// playlist events — the intro and the main video play as a playlist.
export const ANALYTIC_TYPE_LABELS = Object.freeze({
  0: "Brochure Printed",
  1: "Video Loaded",
  2: "Video Started",
  3: "Percent Reached",
  4: "Step Reached",
  5: "Video End",
  6: "Playlist Started",
  7: "Playlist Item Reached",
  8: "Playlist Ended",
})

export const ANALYTIC_TYPE_TO_EVENT = Object.freeze({
  0: playerEventTypes.BROCHURE_PRINTED,
  1: playerEventTypes.VIDEO_LOADED,
  2: playerEventTypes.VIDEO_STARTED,
  3: playerEventTypes.PERCENT_REACHED,
  4: playerEventTypes.STEP_REACHED,
  5: playerEventTypes.VIDEO_ENDED,
})

export const PLAYER_STATE_TO_EVENT = Object.freeze({
  "-1": playerEventTypes.PLAYER_WAITING,
  0: playerEventTypes.PLAYER_READY,
  1: playerEventTypes.PLAYER_PLAYED,
  2: playerEventTypes.PLAYER_PAUSED,
})

// Singleton loader — every video on the page shares one script tag/promise.
let viewMediaScriptPromise = null

export const loadViewMediaScript = () => {
  if (window.OndemandPlayerApi) return Promise.resolve()

  if (!viewMediaScriptPromise) {
    viewMediaScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = VIEWMEDICA_SCRIPT_SRC
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () => {
        viewMediaScriptPromise = null
        reject(new Error("failed to load vm.js"))
      }
      document.head.appendChild(script)
    })
  }

  return viewMediaScriptPromise
}
